/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/customer-lifecycle/hooks/useCustomers.ts
 * layer: web
 * area: hooks
 * kind: hook
 * web_root: hooks
 * relpath: web/src/capabilities/customer-lifecycle/hooks/useCustomers.ts
 * filename: useCustomers.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

import { useQuery } from '@tanstack/react-query';
import { api, rowShape, unwrapList } from '@/platform/api-client/client';
import { collectCursorList } from '@/platform/api-client/cursorList';

/** Minimum row every customer picker relies on. */
export interface CustomerListRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface CustomerFilters {
  /** Server-side name/domain/legal-name match (GET /customers?search=). */
  search?: string;
  /** Page size per request (the backend caps a page at 500). */
  limit?: number;
  /** Server-side lifecycle status filter (GET /customers?status=), e.g. 'active'. */
  status?: string;
  /** Disable the query (e.g. until a picker is shown). Defaults to enabled. */
  enabled?: boolean;
}

// Built lazily: pages import this module transitively, and a module-scope
// schema would make every test that stubs the api client fail at import.
let customerRowSchema: ReturnType<typeof rowShape<CustomerListRow>> | undefined;
function customerRow() {
  customerRowSchema ??= rowShape<CustomerListRow>({ id: 'string', name: 'string' });
  return customerRowSchema;
}
const MAX_PAGE = 500;

/**
 * Every customer matching `filters`, following the cursor until hasMore is
 * false. GET /customers is cursor-paginated ({data, hasMore, nextCursor});
 * reading only the first page silently truncated every picker for tenants
 * with more customers than one page (hyp-1w64c). Use this (or useCustomers)
 * instead of calling GET /customers directly — scripts/check-customer-list-calls.mjs
 * enforces that.
 */
/**
 * Every customer matching the filters, following the cursor to the end.
 * Rows are validated for `id` and `name` only; callers that read more of the
 * customer record pass their row type as `T` (e.g. `Customer`), which is the
 * single place that wider shape is trusted.
 */
export async function fetchAllCustomers<T extends { id: string; name: string } = CustomerListRow>(
  filters?: Omit<CustomerFilters, 'enabled'>,
  signal?: AbortSignal
): Promise<T[]> {
  const limit = Math.min(Math.max(filters?.limit ?? MAX_PAGE, 1), MAX_PAGE);
  const search = filters?.search?.trim() || undefined;
  const status = filters?.status || undefined;
  const rows = await collectCursorList(
    async (cursor) => {
      const body = (
        await api.get<unknown>('/customers', {
          params: { limit, search, cursor, ...(status ? { status } : {}) },
          signal,
        })
      ).data;
      // Validate rows first so a drifted envelope is an ApiContractError,
      // not an empty picker.
      unwrapList(body, customerRow(), 'Customers');
      return body;
    },
    customerRow(),
    'Customers',
    signal
  );
  return rows as T[];
}

export function useCustomers<T extends { id: string; name: string } = CustomerListRow>(
  filters?: CustomerFilters
) {
  return useQuery({
    queryKey: [
      'customers',
      filters
        ? { search: filters.search, limit: filters.limit, status: filters.status }
        : undefined,
    ],
    queryFn: ({ signal }): Promise<T[]> => fetchAllCustomers<T>(filters, signal),
    enabled: filters?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => api.get(`/customers/${id}`).then(r => r.data),
    enabled: !!id,
    staleTime: 30_000,
  });
}
